import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { apiGet } from '../client'
import { toBoardDeal } from '../adapters/dealCard'
import type { BoardDeal } from '../adapters/dealCard'
import type { DealPage, Stage } from '../types'
import type { PriorityFilter } from '../../lib/boardView'

/**
 * The board's reads.
 *
 * Searching, filtering and paging happen on the server. The browser used to
 * fetch every deal and filter the array it held, which is honest at thirty
 * deals and a lie at three hundred: it answers "nothing found" for the deals it
 * never fetched, with exactly the same confidence as a real answer.
 *
 * Each column asks for its own page and gets its own `total`, so a header can
 * say eleven while eleven cards are not on screen. The tiles ask for `limit=0`:
 * the counts, no payload.
 */

/** What the whole board is looking at. Applies to every column and to the tiles. */
export interface DealFilters {
  search: string
  archived: boolean
  priority: PriorityFilter
}

/** One column's slice of that. `decision` uses `"none"` for "not decided yet". */
export interface ColumnFilter {
  stage?: Stage
  decision?: string
}

function toQuery(filters: DealFilters, column: ColumnFilter, limit: number): string {
  const params = new URLSearchParams()
  const term = filters.search.trim()
  if (term) params.set('q', term)
  params.set('archived', String(filters.archived))
  // "review" is not a priority, it is a flag on the deal. The chip sits in the
  // same row as high/medium/low on screen; it must not be sent as one.
  if (filters.priority === 'review') params.set('re_evaluation', 'true')
  else if (filters.priority !== 'all') params.set('priority', filters.priority)
  if (column.stage) params.set('stage', column.stage)
  if (column.decision) params.set('decision', column.decision)
  params.set('limit', String(limit))
  return params.toString()
}

export const dealsKey = ['deals'] as const

function useDealPage(filters: DealFilters, column: ColumnFilter, limit: number) {
  const query = toQuery(filters, column, limit)
  return useQuery<DealPage, Error, BoardPage>({
    queryKey: [...dealsKey, query],
    queryFn: () => apiGet<DealPage>(`/deals?${query}`),
    // Without this the board empties on every keystroke and every "show more",
    // which reads as "no results" for as long as the request takes.
    placeholderData: keepPreviousData,
    select: (page) => ({
      deals: page.items.map(toBoardDeal),
      total: page.total,
      countsByStage: page.counts_by_stage,
      countsByDecision: page.counts_by_decision,
      reEvaluationCount: page.re_evaluation_count,
    }),
  })
}

export interface BoardPage {
  deals: BoardDeal[]
  total: number
  countsByStage: Partial<Record<Stage, number>>
  countsByDecision: Record<string, number>
  reEvaluationCount: number
}

/** The numbers above the board, over the whole filtered set. No cards fetched. */
export function useDealCounts(filters: DealFilters) {
  return useDealPage(filters, {}, 0)
}

/** One column: its first `limit` cards, and how many it has in total. */
export function useDealColumn(filters: DealFilters, column: ColumnFilter, limit: number) {
  return useDealPage(filters, column, limit)
}

/**
 * Company names for the upload dialog's datalist.
 *
 * A datalist is a suggestion, not an authority: the field accepts a name that
 * is not in it, so a bounded fetch is honest here in a way it would not be on
 * the board. Bounded it is — an account with more deals than this gets fewer
 * suggestions, never a wrong one.
 */
export function useDealNameSuggestions() {
  const page = useDealPage({ search: '', archived: false, priority: 'all' }, {}, SUGGESTION_LIMIT)
  return page.data?.deals ?? []
}

const SUGGESTION_LIMIT = 200

/** The archive, which has no columns — one flat page. */
export function useArchivedDeals(filters: DealFilters, limit: number) {
  return useDealPage({ ...filters, archived: true }, {}, limit)
}
